import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate } from '../../lib/format';
import MultiSelectFilter from '../../components/workspace/MultiSelectFilter';
import { useReportScope } from '../../lib/useReportScope';
import ReportPdfButton, { ExcelIcon, PdfIcon, excelBtnStyle, downloadExcel } from '../../components/workspace/ReportPdfButton';

const STATUS_LABEL = {
  pending_self:     'Self Rating Pending',
  pending_manager:  'With Manager',
  pending_approver: 'Awaiting Final Approval',
  approved:         'Approved',
  rejected:         'Rejected',
  closed:           'Closed',
};

const STATUS_COLOR = {
  pending_self:     '#f0a93b',
  pending_manager:  '#6f7bff',
  pending_approver: '#06b6d4',
  approved:         '#4ad295',
  rejected:         '#ff6b6b',
};

const labelStyle = {
  fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || '#6b7280';
  const label = STATUS_LABEL[status] || status;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: `${color}22`, color,
    }}>
      {label}
    </span>
  );
}

function ScoreCell({ score, itemCount, emptyLabel }) {
  if (score == null || score === '') {
    const count = Number(itemCount) || 0;
    if (count === 0 && emptyLabel) {
      return <span style={{ color: 'var(--text-muted)', fontSize: 11, fontStyle: 'italic' }}>{emptyLabel}</span>;
    }
    if (count > 0) {
      return <span style={{ color: '#f0a93b', fontSize: 11, fontWeight: 600 }} title="Items exist but haven't been rated by the manager yet">Not rated</span>;
    }
    return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  }
  const n = parseFloat(score);
  const color = n >= 4 ? '#4ad295' : n >= 3 ? '#6f7bff' : n >= 2 ? '#f0a93b' : '#ff6b6b';
  return (
    <span style={{ fontWeight: 700, color }}>
      {n.toFixed(2)} <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)' }}>/5</span>
    </span>
  );
}

function Th({ children, right }) {
  return (
    <th style={{
      padding: '10px 14px', textAlign: right ? 'right' : 'left',
      fontWeight: 600, color: 'var(--text-secondary)',
      whiteSpace: 'nowrap', fontSize: 12,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {children}
    </th>
  );
}

export default function PerformanceReport() {
  const { appId } = useParams();
  const api = useApi();
  const { me } = useWorkspace();
  const { isAdmin, scopeIds, isManager } = useReportScope(appId);
  const isMultiUser = isAdmin || isManager;

  const [cycles,         setCycles]         = useState([]);
  const [selectedCycle,  setSelectedCycle]  = useState('');
  const [reviews,        setReviews]        = useState([]);
  const [filterMember,   setFilterMember]   = useState([]);
  const [filterStatus,   setFilterStatus]   = useState('');
  const [loading,        setLoading]        = useState(false);
  const [cyclesLoading,  setCyclesLoading]  = useState(true);
  const [error,          setError]          = useState(null);

  useEffect(() => {
    api.listReviewCycles(appId)
      .then(r => {
        const items = r.items || [];
        setCycles(items);
        if (items.length > 0) {
          const today = new Date().toISOString().slice(0, 10);
          const active = items.find(c => c.start_date <= today && c.end_date >= today) || items[items.length - 1];
          setSelectedCycle(String(active.id));
        }
      })
      .catch(() => {})
      .finally(() => setCyclesLoading(false));
  }, [appId]);

  const loadReviews = useCallback(async () => {
    if (!selectedCycle) return;
    setLoading(true);
    setError(null);
    try {
      let items = [];
      if (isAdmin) {
        const res = await api.listAllReviews(appId, { cycle_id: selectedCycle });
        items = res.items || [];
      } else {
        const [teamRes, selfRes] = await Promise.all([
          api.listTeamReviews(appId, { cycle_id: selectedCycle }),
          api.listMyReviews(appId, { cycle_id: selectedCycle }),
        ]);
        const all = [...(teamRes.items || []), ...(selfRes.items || [])];
        const seen = new Set();
        items = all.filter(r => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return !selectedCycle || String(r.cycle_id) === selectedCycle;
        });
      }
      setReviews(items);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [appId, selectedCycle, isAdmin]);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  const memberName = emp => {
    if (!emp) return '—';
    return [emp.first_name, emp.last_name].filter(Boolean).join(' ') || emp.email || '—';
  };

  const uniqueMembers = useMemo(() => {
    const seen = new Map();
    reviews.forEach(r => {
      if (r.user && r.platform_user_id != null) {
        seen.set(r.platform_user_id, memberName(r.user));
      }
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [reviews]);

  const filtered = useMemo(() => {
    let list = [...reviews];
    if (scopeIds) list = list.filter(r => scopeIds.has(String(r.platform_user_id)));
    if (filterMember.length > 0) list = list.filter(r => filterMember.includes(String(r.platform_user_id)));
    // Closed reviews only appear when the Closed status is picked
    list = list.filter(r => (filterStatus ? r.status === filterStatus : r.status !== 'closed'));
    return list;
  }, [reviews, filterMember, filterStatus, scopeIds]);

  const summary = useMemo(() => ({
    total:     filtered.length,
    selfPend:  filtered.filter(r => r.status === 'pending_self').length,
    withMgr:   filtered.filter(r => r.status === 'pending_manager').length,
    withApprv: filtered.filter(r => r.status === 'pending_approver').length,
    approved:  filtered.filter(r => r.status === 'approved').length,
    rejected:  filtered.filter(r => r.status === 'rejected').length,
  }), [filtered]);

  const cycleName = useMemo(() => {
    const c = cycles.find(c => String(c.id) === selectedCycle);
    return c ? `${c.cycle_name} (${c.year})` : '';
  }, [cycles, selectedCycle]);

  const pdfTable = useMemo(() => ({
    headers: ['Employee', 'Role', 'Status', 'Goals Score', 'Skills Score', 'Values Score', 'Overall Score', 'Created'],
    rows: filtered.map(r => [
      memberName(r.user),
      r.role_title || '—',
      STATUS_LABEL[r.status] || r.status,
      r.goals_score != null ? `${parseFloat(r.goals_score).toFixed(2)} / 5` : '—',
      r.skills_score != null ? `${parseFloat(r.skills_score).toFixed(2)} / 5` : '—',
      r.values_score != null ? `${parseFloat(r.values_score).toFixed(2)} / 5` : '—',
      r.final_score != null ? `${parseFloat(r.final_score).toFixed(2)} / 5` : '—',
      r.created_at ? formatDate(r.created_at.slice(0, 10)) : '—',
    ]),
  }), [filtered]);

  const pdfCards = useMemo(() => !loading ? [
    { label: 'Total',          value: summary.total,     color: 'var(--text-primary)' },
    { label: 'Self Pending',   value: summary.selfPend,  color: '#f0a93b' },
    { label: 'With Manager',   value: summary.withMgr,   color: '#6f7bff' },
    { label: 'Final Approval', value: summary.withApprv, color: '#06b6d4' },
    { label: 'Approved',       value: summary.approved,  color: '#4ad295' },
    { label: 'Rejected',       value: summary.rejected,  color: '#ff6b6b' },
  ] : [], [loading, summary]);

  return (
    <div className="tasks-page">
      <div className="tl-header">
        <div>
          <h1 className="page-title tl-title-row">Performance Report</h1>
          <p className="tl-subtitle">Review status and scores for all members in a selected cycle.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => downloadExcel('performance-report.xls', 'Performance Report', cycleName ? `Review Cycle: ${cycleName}` : '', pdfTable.headers, pdfTable.rows)}
            disabled={filtered.length === 0}
            style={{ ...excelBtnStyle, opacity: filtered.length === 0 ? 0.4 : 1 }}
          >
            <ExcelIcon /> Excel
          </button>
          <ReportPdfButton
            title="Performance Report"
            subtitle={cycleName ? `Review Cycle: ${cycleName}` : ''}
            summaryCards={pdfCards}
            table={pdfTable}
            icon={<PdfIcon />}
          />
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap',
        marginBottom: 24, padding: '16px 20px',
        background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Review Cycle</label>
          <select
            value={selectedCycle}
            onChange={e => setSelectedCycle(e.target.value)}
            disabled={cyclesLoading}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minWidth: 200 }}
          >
            {cycles.length === 0 && <option value="">No cycles</option>}
            {cycles.map(c => (
              <option key={c.id} value={String(c.id)}>{c.cycle_name} ({c.year})</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Member</label>
          <MultiSelectFilter
            options={uniqueMembers.filter(m => !scopeIds || scopeIds.has(String(m.id))).map(m => ({ value: String(m.id), label: m.name }))}
            selected={filterMember}
            onChange={setFilterMember}
            placeholder="All Members"
            minWidth={200}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minWidth: 200 }}
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary cards */}
      {!loading && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Total',            value: summary.total,     color: 'var(--text-primary)' },
            { label: 'Self Pending',     value: summary.selfPend,  color: '#f0a93b' },
            { label: 'With Manager',     value: summary.withMgr,   color: '#6f7bff' },
            { label: 'Final Approval',   value: summary.withApprv, color: '#06b6d4' },
            { label: 'Approved',         value: summary.approved,  color: '#4ad295' },
            { label: 'Rejected',         value: summary.rejected,  color: '#ff6b6b' },
          ].map(c => (
            <div key={c.label} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '12px 20px', minWidth: 110,
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '12px 16px', color: '#ef4444', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!selectedCycle && !cyclesLoading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No review cycles found. Create one under Organisation → Review Cycles.
        </div>
      ) : loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No reviews found for the selected filters.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)' }}>
                <Th>Employee</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th right>Goals Score</Th>
                <Th right>Skills Score</Th>
                <Th right>Values Score</Th>
                <Th right>Overall Score</Th>
                <Th>Created</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} style={{
                  background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text-primary)', maxWidth: 200, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {memberName(r.user)}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', maxWidth: 180, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {r.role_title || '—'}
                  </td>
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <StatusBadge status={r.status} />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <ScoreCell score={r.goals_score} itemCount={r.goal_items_count} emptyLabel="No goals set" />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <ScoreCell score={r.skills_score} itemCount={r.skill_items_count} emptyLabel="No skills required" />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <ScoreCell score={r.values_score} itemCount={r.value_items_count} emptyLabel="No values set" />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    <ScoreCell score={r.final_score} />
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {r.created_at ? formatDate(r.created_at.slice(0, 10)) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}